import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// One-time cinematic video intro. The showreel plays full-bleed, then the
// whole panel lifts upward to reveal the home page beneath it.
export const CinematicIntro = ({ onDone }) => {
  const [revealing, setRevealing] = useState(false);
  const [isMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const doneRef = useRef(false);
  const videoRef = useRef(null);
  const bgVideoRef = useRef(null);

  // Single audio-free source for every device. On mobile the same footage is
  // rendered twice: a blurred, scaled copy fills the screen behind the full
  // horizontal video (contained, never cropped) — a live "blurred fill" so the
  // backdrop is literally the same video, adapting to any phone aspect ratio.
  const src = "/hero-showreel-noaudio.mp4";
  const poster = "/hero-showreel-poster.jpg";

  // Feather the top/bottom edges of the mobile foreground video so it dissolves
  // into the blurred fill behind it instead of showing a hard rectangular seam.
  const MASK =
    "linear-gradient(to bottom, transparent 0%, #000 20%, #000 80%, transparent 100%)";

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone?.();
  };

  // Trigger the upward reveal, then finish once the slide completes.
  const startReveal = () => {
    if (doneRef.current || revealing) return;
    setRevealing(true);
  };

  useEffect(() => {
    // iOS-safe autoplay: muted + inline attributes must be set on the element.
    const prep = (v) => {
      if (!v) return;
      v.muted = true;
      v.defaultMuted = true;
      v.setAttribute("muted", "");
      v.setAttribute("playsinline", "");
      v.setAttribute("webkit-playsinline", "");
      v.playbackRate = 3;
      const p = v.play?.();
      return p;
    };
    prep(bgVideoRef.current);
    const p = prep(videoRef.current);
    // If autoplay is blocked (e.g. iOS Low Power Mode), skip straight to the
    // site instead of leaving a stuck play button.
    if (p && typeof p.catch === "function") p.catch(() => finish());
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[10050] bg-void overflow-hidden"
      data-testid="cinematic-intro"
      initial={{ opacity: 1 }}
      animate={{ opacity: revealing ? 0 : 1 }}
      transition={{ duration: 2, ease: "easeInOut" }}
      style={{ pointerEvents: revealing ? "none" : "auto" }}
      onAnimationComplete={() => {
        if (revealing) finish();
      }}
    >
      {/* Blurred same-footage fill (mobile only). A scaled + blurred copy of the
          exact same video covers the whole screen. Brightness is kept close to
          the foreground so the backdrop reads as one continuous piece. */}
      {isMobile && (
        <video
          ref={bgVideoRef}
          src={src}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: "scale(1.6)", filter: "blur(26px) brightness(0.85) saturate(1.1)" }}
          muted
          autoPlay
          playsInline
          preload="auto"
          aria-hidden="true"
        />
      )}

      {/* Main showreel. Desktop fills the frame (object-cover). Mobile shows the
          full horizontal video as a centred 16:9 band whose top/bottom edges are
          feathered with a mask so it melts into the blurred fill — one unit. */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className={isMobile ? "absolute left-0 w-full" : "absolute inset-0 w-full h-full object-cover"}
        style={
          isMobile
            ? {
                top: "50%",
                transform: "translateY(-50%)",
                aspectRatio: "16 / 9",
                WebkitMaskImage: MASK,
                maskImage: MASK,
              }
            : undefined
        }
        muted
        autoPlay
        playsInline
        preload="auto"
        data-testid="intro-video"
        onLoadedMetadata={(e) => { e.currentTarget.playbackRate = 3; }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (!v.duration) return;
          // End the intro 2 seconds before the video actually finishes.
          if (v.currentTime >= v.duration - 2) startReveal();
        }}
        onEnded={startReveal}
        onError={finish}
      />

      {/* Very subtle vignette for depth (kept light so it does not create a
          brightness edge between the video and its blurred fill). */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-void/20 via-transparent to-void/25" />

      {/* Skip */}
      <button
        onClick={startReveal}
        data-testid="intro-skip"
        className="absolute bottom-8 right-8 z-[10] text-[0.6rem] tracking-[0.35em] uppercase text-white/60 hover:text-gold transition-colors border border-white/20 px-4 py-2 backdrop-blur-sm"
      >
        Skip Intro
      </button>
    </motion.div>
  );
};
